import type { CustomerSpdState, CustomerSpdResponseItem, StateTree, CustomerSpd } from '@makemydeal/dr-dash-types';
import { createSelector } from 'reselect';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as offerReduxSelectors from './offerRedux';

export const getCustomerSpdState = (state: StateTree) => state.customerSpd;

export const getCustomerSpdStateWithFallback = createSelector(
    [getCustomerSpdState],
    (customerSpdState) => customerSpdState || ({} as CustomerSpdState)
);

export const getCheckCustomerSpdLoading = (state: StateTree): boolean => {
    return getCustomerSpdStateWithFallback(state).checkCustomerLoading;
};

export const getCheckCustomerSpdResult =
    (commonConsumerId: string) =>
    (state: StateTree): CustomerSpdResponseItem | undefined => {
        return getCustomerSpdStateWithFallback(state).checkCustomerResponse[commonConsumerId];
    };

export const getCheckCustomerSpdError = () => (state: StateTree) => {
    return getCustomerSpdStateWithFallback(state).checkCustomerError;
};

export const getCustomerSpdData = (
    state: StateTree,
    commonConsumerId: string
): Omit<CustomerSpd, 'commonConsumerId'> | undefined => {
    return getCustomerSpdStateWithFallback(state).participantsSpd?.[commonConsumerId];
};

export const getCustomerSpdLoading =
    () =>
    (state: StateTree): boolean => {
        return getCustomerSpdStateWithFallback(state).getCustomerLoading;
    };

export const getCustomerSpdResult =
    (commonConsumerId: string) =>
    (state: StateTree): CustomerSpd | undefined => {
        return getCustomerSpdStateWithFallback(state).getCustomerResponse[commonConsumerId];
    };

export const getCustomerSpdError =
    (commonConsumerId: string) =>
    (state: StateTree): CustomerSpd | undefined => {
        return getCustomerSpdStateWithFallback(state).getCustomerError?.[commonConsumerId];
    };

/** Whether the requesting user has Desking permission. Drives the "Show Data" CTA. */
export const getHasDeskingPermission = (state: StateTree): boolean => {
    return getCustomerSpdStateWithFallback(state).canDesk;
};

export const getCustomerAPISuccess = (state: StateTree, commonConsumerId: string): boolean => {
    return getCustomerSpdStateWithFallback(state).getCustomerAPISuccess[commonConsumerId] || false;
};

export const getAllCustomersSpdData = (state: StateTree): Record<string, Omit<CustomerSpd, 'commonConsumerId'>> => {
    return getCustomerSpdStateWithFallback(state).participantsSpd || {};
};

export const getSpdDataForContracting = (state: StateTree) => {
    const buyerCommonConsumerId = offerReduxSelectors.getShopperCommonConsumerId(state);
    const coBuyerCommonConsumerId = offerReduxSelectors.getCoBuyerCommonConsumerId(state);

    return {
        buyer: getCustomerSpdData(state, buyerCommonConsumerId),
        coBuyer: coBuyerCommonConsumerId && getCustomerSpdData(state, coBuyerCommonConsumerId)
    };
};

export const getSaveCustomerSpdLoading =
    () =>
    (state: StateTree): boolean => {
        return getCustomerSpdStateWithFallback(state).updateCustomerSpdLoading;
    };

export const getSaveCustomerSpdHasError = (state: StateTree) => {
    const error = getCustomerSpdStateWithFallback(state).updateCustomerSpdError;
    if (error && !Object.keys(error).length) {
        return false;
    }
    return Boolean(error);
};

export const getBuyerHasDriversLicense = (state: StateTree): boolean => {
    const buyerCommonConsumerId = offerReduxSelectors.getShopperCommonConsumerId(state);
    if (!buyerCommonConsumerId) return false;
    const checkResult = getCustomerSpdStateWithFallback(state).checkCustomerResponse?.[buyerCommonConsumerId];
    return checkResult?.driversLicenseNumber === true;
};

/**
 * Returns true when identity data is complete for soft pull consent:
 * - Has DL, OR
 * - (When enableSoftPullConsentWithoutDLMV is ON) Has firstName, lastName, dateOfBirth, and zip
 */
export const getHasIdentityDataCompleteness = (state: StateTree): boolean => {
    const hasDriversLicense = getBuyerHasDriversLicense(state);
    const isEnableSoftPullConsentWithoutDLMV = featureToggleSelectors.isEnableSoftPullConsentWithoutDLMV(state);

    if (hasDriversLicense || !isEnableSoftPullConsentWithoutDLMV) return hasDriversLicense;

    const shopperInfo = offerReduxSelectors.getShopperInfo(state);
    const buyerCommonConsumerId = offerReduxSelectors.getShopperCommonConsumerId(state);
    const checkSpdData = buyerCommonConsumerId ? getCheckCustomerSpdResult(buyerCommonConsumerId)(state) : undefined;

    const hasFirstName = Boolean(shopperInfo && shopperInfo.firstName);
    const hasLastName = Boolean(shopperInfo && shopperInfo.lastName);
    const hasDateOfBirth = Boolean(checkSpdData && checkSpdData.dateOfBirth);
    const hasZip = Boolean(shopperInfo && shopperInfo.zip);

    return hasFirstName && hasLastName && hasDateOfBirth && hasZip;
};
